/**
 * What each aircraft can do, estimated from what it has done.
 *
 * A planning estimate, kept apart from the live return margin on the Command
 * screen and labelled as such: that margin comes from the charge and draw the
 * aircraft reports in flight and deliberately ignores wind, and this does not.
 * Every figure here carries the number of flights it rests on, because a
 * range off two flights read with the confidence of one off forty is how an
 * aircraft gets sent somewhere it cannot come back from.
 */

import { useEffect, useState } from 'react';

import { getRangeIntoWind, type PerformanceReport, type RangeIntoWind } from '@/api/analytics';

import {
  Bars,
  Chip,
  DANGER,
  date,
  Empty,
  MUTED,
  num,
  OK,
  Panel,
  Table,
  WARN,
  WHITE,
} from './dataUi';

function confidenceTone(text: string): string {
  if (text === 'Good') return OK;
  return text.startsWith('No pack') ? MUTED : WARN;
}

function RangeIntoWindCalculator({
  report,
  includeSimulated,
}: {
  report: PerformanceReport;
  includeSimulated: boolean;
}) {
  const modelled = report.energy.filter((m) => m.rangeKm !== null);
  const [itemId, setItemId] = useState(modelled[0]?.itemId ?? '');
  const [headwind, setHeadwind] = useState(5);
  const [answer, setAnswer] = useState<RangeIntoWind | null>(null);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void getRangeIntoWind(itemId, headwind, includeSimulated).then((result) => {
        if (!cancelled) setAnswer(result.ok ? result.data : null);
      });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [itemId, headwind, includeSimulated]);

  if (modelled.length === 0) {
    return <Empty>No aircraft has a range estimate yet, so there is nothing to put into wind.</Empty>;
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1">
          <span className="label-mono text-[8px]" style={{ color: MUTED }}>
            AIRCRAFT
          </span>
          <select
            value={itemId}
            onChange={(event) => setItemId(event.target.value)}
            className="well h-8 rounded-[9px] px-2 text-[12px] outline-none"
          >
            {modelled.map((m) => (
              <option key={m.itemId} value={m.itemId}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-[220px] flex-1 flex-col gap-1">
          <span className="label-mono text-[8px]" style={{ color: MUTED }}>
            HEADWIND {headwind} M/S
          </span>
          <input
            type="range"
            min={0}
            max={20}
            step={1}
            value={headwind}
            onChange={(event) => setHeadwind(Number(event.target.value))}
          />
        </label>
        <div className="flex flex-col">
          <span className="label-mono text-[8px]" style={{ color: MUTED }}>
            RANGE TO RESERVE
          </span>
          <span className="tnum text-[28px] font-semibold leading-none" style={{ color: WHITE }}>
            {answer?.rangeKm === null || answer?.rangeKm === undefined ? 'n/a' : `${answer.rangeKm.toFixed(1)} km`}
          </span>
          {answer?.stillAirKm ? (
            <span className="label-mono mt-1 text-[8.5px]" style={{ color: MUTED }}>
              {answer.stillAirKm.toFixed(1)} KM IN STILL AIR
            </span>
          ) : null}
        </div>
      </div>
      <p className="text-[10.5px]" style={{ color: MUTED }}>
        Out and back is half this. A tailwind is not credited: the leg home is into whatever helped
        on the way out.{' '}
        {report.wind.costPerMs !== null
          ? `Each m/s of headwind has cost about ${(report.wind.costPerMs * 100).toFixed(1)}% more energy per km, fitted from ${report.wind.samples} flights.`
          : report.wind.note}
      </p>
    </div>
  );
}

export function PerformancePanel({
  report,
  includeSimulated,
}: {
  report: PerformanceReport;
  includeSimulated: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      <p
        className="rounded-[12px] border px-3 py-2 text-[11.5px]"
        role="note"
        style={{
          color: WHITE,
          borderColor: `color-mix(in oklch, ${WARN} 40%, transparent)`,
          background: `color-mix(in oklch, ${WARN} 8%, transparent)`,
        }}
      >
        {report.basis}
      </p>

      <Panel
        title="RANGE AND ENDURANCE TO A LANDING RESERVE"
        subtitle="The headline is the conservative edge: usable energy over the high end of what each aircraft has used, so most sorties beat it. The mean is beside it."
      >
        <Table
          head={['AIRCRAFT', 'FLIGHTS USED', 'WH PER KM', 'PACK', 'RANGE', 'ENDURANCE', 'CONFIDENCE']}
          empty="No aircraft has flown with its energy recorded yet."
          rows={report.energy.map((m) => [
            m.name,
            `${m.samples} of ${m.flights}`,
            m.whPerKm === null ? 'n/a' : `${m.whPerKm.toFixed(1)} ± ${num(m.whPerKmSd, 1)}`,
            m.packWh === null
              ? 'unknown'
              : `${m.packWh.toFixed(0)} Wh ${m.packSource === 'spec' ? '(spec)' : `(from ${m.packSamples} flights)`}, ${m.reservePct}% kept`,
            m.rangeKm === null ? 'n/a' : `${m.rangeKm.toFixed(1)} km (mean ${num(m.rangeMeanKm, 1)})`,
            m.enduranceMin === null
              ? 'n/a'
              : `${m.enduranceMin.toFixed(0)} min (mean ${num(m.enduranceMeanMin, 0)})`,
            <Chip key="c" tone={confidenceTone(m.confidence)}>
              {m.confidence.toUpperCase()}
            </Chip>,
          ])}
        />
      </Panel>

      <Panel title="INTO A HEADWIND">
        <RangeIntoWindCalculator report={report} includeSimulated={includeSimulated} />
      </Panel>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))' }}>
        <Panel
          title="TEMPERATURE"
          subtitle={
            report.temperature.referenceC !== null
              ? `Range in each band, against the warmest band flown (${report.temperature.referenceC} to ${report.temperature.referenceC + 10} °C). Each flight is compared with its own aircraft, so this shows the weather and not the mix of airframes.`
              : undefined
          }
        >
          <Bars
            empty={report.temperature.note ?? 'No data.'}
            data={report.temperature.bands.map((b) => ({
              label: `${b.fromC} to ${b.toC} °C`,
              value: b.relativeRange,
              display: `${(b.relativeRange * 100).toFixed(0)}% (${b.flights})`,
              tone: b.relativeRange < 0.85 ? WARN : undefined,
            }))}
          />
        </Panel>
        <Panel
          title="PACKS"
          subtitle="For parts recorded as a battery. Cycles and capacity count only flights since the pack was fitted. Resistance is sag over peak current: a trend, not a bench figure."
        >
          <Table
            head={['PACK', 'ON', 'CYCLES', 'HOLDS', 'TREND', 'MOHM']}
            empty="No batteries recorded as parts yet. Fit one on the Register tab to track it."
            rows={report.packs.map((p) => [
              <span key="p" title={p.note ?? undefined}>
                {p.label}
                {p.serial ? ` ${p.serial}` : ''}
              </span>,
              p.airframe ?? 'n/a',
              p.cycles,
              p.measuredWh === null ? 'n/a' : `${p.measuredWh.toFixed(0)} Wh`,
              <span
                key="t"
                style={{ color: p.capacityTrendPct !== null && p.capacityTrendPct < -15 ? DANGER : MUTED }}
                title={p.note ?? undefined}
              >
                {p.capacityTrendPct === null ? 'too few' : `${p.capacityTrendPct.toFixed(0)}%`}
              </span>,
              num(p.resistanceMohm, 0),
            ])}
          />
        </Panel>
      </div>

      <Panel
        title="AGAINST THE SPEC SHEET"
        subtitle="Endurance and range are held against the model above, not the longest sortie: a spec quotes a full pack flown out. Ceiling, speed and wind limits are flagged only when an aircraft went past them."
      >
        {report.specs.length === 0 ? (
          <Empty>No model with flights yet. Enter spec sheets on the Register tab.</Empty>
        ) : (
          <div className="flex flex-col gap-3">
            {report.specs.map((s) => (
              <div key={s.platformId} className="flex flex-col gap-1">
                <div className="flex items-center gap-2 text-[12px]">
                  <span style={{ color: WHITE }}>{s.platform}</span>
                  <span className="label-mono text-[8px]" style={{ color: MUTED }}>
                    {s.flights} FLIGHTS
                  </span>
                  {s.exceeded.length ? <Chip tone={DANGER}>FLOWN PAST ITS LIMITS</Chip> : null}
                </div>
                {s.performance.length === 0 && s.capability.length === 0 ? (
                  <Empty>No claims entered for this model.</Empty>
                ) : (
                  <Table
                    head={['CLAIM', 'SPEC', 'THIS FLEET', '']}
                    empty=""
                    rows={[
                      ...s.performance.map((p) => [
                        p.metric,
                        `${p.claimed} ${p.unit}`,
                        p.modelled === null ? 'no model yet' : `${p.modelled.toFixed(1)} ${p.unit}`,
                        p.percentOfClaim === null ? '' : `${p.percentOfClaim.toFixed(0)}% of claim`,
                      ]),
                      ...s.capability.map((c) => [
                        c.metric,
                        `${c.claimed} ${c.unit}`,
                        `${c.observed} ${c.unit} highest seen`,
                        <span key="e" style={{ color: c.exceeded ? DANGER : MUTED }}>
                          {c.exceeded ? 'EXCEEDED' : 'within'}
                        </span>,
                      ]),
                    ]}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title="FLIGHTS OFF THEIR OWN NORM"
        subtitle="Compared with the same aircraft's other flights, not with the fleet: a heavy lifter's normal draw is not an anomaly."
      >
        <Table
          head={['AIRCRAFT', 'FLIGHT', 'WHAT', 'VALUE', 'ITS NORMAL', 'SIGMA']}
          empty="Nothing unusual, or not enough flights per aircraft to say (four or more)."
          rows={report.anomalies.map((a) => [
            a.name ?? 'n/a',
            date(a.startTime),
            a.label,
            a.value.toFixed(2),
            `${a.baseline.toFixed(2)} over ${a.samples - 1}`,
            <span key="s" style={{ color: a.sigma >= 3 ? DANGER : WARN }}>
              {a.sigma.toFixed(1)}
            </span>,
          ])}
        />
      </Panel>
    </div>
  );
}
